export const visitSuccessTitle = ({ context, status, t } = {}) => {
  if (context === 'request') {
    return 'Sua visita foi solicitada';
  }
  if (context === 'register') {
    return typeof t === 'function'
      ? t('VISITS.SUCCESS.REGISTERED')
      : 'Sua visita foi registrada!';
  }
  if (context === 'reschedule') {
    return typeof t === 'function'
      ? t('VISITS.SUCCESS.RESCHEDULED')
      : 'Sua visita foi reagendada!';
  }
  if (status === 'confirmada') {
    return typeof t === 'function'
      ? t('VISITS.SUCCESS.SCHEDULED_CONFIRMED')
      : 'Sua visita foi agendada e confirmada!';
  }
  return typeof t === 'function'
    ? t('VISITS.SUCCESS.SCHEDULED')
    : 'Sua visita foi agendada!';
};

export const visitFinishTitle = ({ status, t } = {}) => {
  if (status === 'nao_compareceu') {
    return typeof t === 'function'
      ? t('VISITS.SUCCESS.NOT_DONE')
      : 'Visita encerrada como não realizada';
  }

  return typeof t === 'function'
    ? t('VISITS.SUCCESS.FINISHED')
    : 'Visita finalizada com sucesso!';
};

export const formatVisitDateTime = value => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(date);
};

const toText = value => String(value || '').trim();

const firstPresent = values =>
  values.find(value => value !== undefined && value !== null && value !== '');

const additionalAttributesFor = visit =>
  visit?.additionalAttributes || visit?.additional_attributes || {};

export const visitRequestFeedbackMessage =
  'A Secretaria de Vendas vai entrar em contato para te passar os detalhes dessa visita. Sugerimos que você aguarde esse contato antes de confirmar a visita com o seu cliente.';

export const shouldShowRequestVisitFeedback = ({ context, visit } = {}) => {
  const status = visit?.status || visit?.statusLabel || visit?.status_label;
  const attrs = additionalAttributesFor(visit);

  return (
    context === 'request' ||
    attrs.requestVisitFeedback === true ||
    attrs.request_visit_feedback === true ||
    attrs.visitRequestEnabled === true ||
    attrs.visit_request_enabled === true ||
    (context === 'schedule' && ['solicitada', 'agendada'].includes(status))
  );
};

export const visitRequestFeedbackRows = visit => [
  {
    icon: 'i-lucide-calendar-days',
    label: 'Data da visita',
    value: formatVisitDateTime(visit?.scheduledAt || visit?.scheduled_at),
  },
  {
    icon: 'i-lucide-map-pin',
    label: 'Imóvel',
    value:
      [
        firstPresent([
          visit?.imovel?.titulo,
          visit?.imovel?.endereco,
          visit?.propertyTitle,
          visit?.property_title,
        ]),
        visit?.imovel?.bairro,
        visit?.imovel?.cidade,
      ]
        .filter(Boolean)
        .map(toText)
        .join(', ') || 'Imóvel não informado',
  },
];

const PENDING_CONFIRMATION_STATUSES = new Set(['solicitada', 'agendada']);

const confirmationMetadataFor = visit => {
  const attrs = additionalAttributesFor(visit);
  return attrs.visitConfirmation || attrs.visit_confirmation || {};
};

const visitExpirationDateFor = visit => {
  const attrs = additionalAttributesFor(visit);
  const confirmation = confirmationMetadataFor(visit);

  return firstPresent([
    visit?.visitExpirationDate,
    visit?.visit_expiration_date,
    visit?.confirmationDueAt,
    visit?.confirmation_due_at,
    attrs.visitExpirationDate,
    attrs.visit_expiration_date,
    attrs.confirmationDueAt,
    attrs.confirmation_due_at,
    confirmation.expiresAt,
    confirmation.expires_at,
    confirmation.dueAt,
    confirmation.due_at,
  ]);
};

export const visitConfirmationStatus = visit => {
  const status = visit?.status || visit?.statusLabel;
  if (status === 'confirmada') {
    return {
      icon: 'i-lucide-circle-check',
      label: 'Visita confirmada',
      tone: 'text-emerald-700 bg-emerald-50',
    };
  }

  if (status === 'realizada') {
    return {
      icon: 'i-lucide-badge-check',
      label: 'Visita realizada',
      tone: 'text-emerald-700 bg-emerald-50',
    };
  }

  if (status === 'nao_compareceu') {
    return {
      icon: 'i-lucide-circle-x',
      label: 'Visita não realizada',
      tone: 'text-rose-700 bg-rose-50',
    };
  }

  return {
    icon: 'i-lucide-clock-3',
    label: 'Aguardando confirmação',
    tone: 'text-amber-700 bg-amber-50',
  };
};

export const visitConfirmationDueDateAlert = visit => {
  const status = visit?.status || visit?.statusLabel;
  if (!PENDING_CONFIRMATION_STATUSES.has(status)) {
    return { visible: false };
  }

  const dueDate = visitExpirationDateFor(visit);
  const formattedDate = formatVisitDateTime(dueDate);
  if (formattedDate === '—') {
    return { visible: false };
  }

  return {
    visible: true,
    icon: 'i-lucide-triangle-alert',
    title: 'Confirmação pendente',
    label: `Confirme até ${formattedDate} para manter o horário.`,
  };
};

export const visitSummaryRows = visit => [
  {
    icon: 'i-lucide-user-round',
    label: 'Cliente',
    value:
      visit?.contact?.name || visit?.contactName || 'Cliente não informado',
  },
  {
    icon: 'i-lucide-building-2',
    label: 'Imóvel',
    value:
      visit?.imovel?.titulo ||
      visit?.imovel?.endereco ||
      visit?.propertyTitle ||
      'Imóvel não informado',
  },
  {
    icon: 'i-lucide-map-pin',
    label: 'Endereço',
    value:
      [visit?.imovel?.endereco, visit?.imovel?.bairro, visit?.imovel?.cidade]
        .filter(Boolean)
        .join(', ') || 'Endereço não informado',
  },
  {
    icon: 'i-lucide-calendar-days',
    label: 'Data e hora',
    value: formatVisitDateTime(visit?.scheduledAt || visit?.scheduled_at),
  },
  {
    icon: 'i-lucide-map-pinned',
    label: 'Tipo de visita',
    value:
      visit?.buyerVisitIntention ||
      visit?.buyer_visit_intention ||
      'Intenção não informada',
  },
];
